import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Plus, Pencil, Trash2, Clock, Navigation, Layers, BadgeDollarSign, Inbox, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (n, d = 2) => `$${(Number(n) || 0).toLocaleString("en-AU", { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const EMPTY = {
  customer_name: "", job_date: "", start_time: "", pickup: "", dropoff: "", mover_name: "",
  hourly_rate: "", mover_hours: "", charged_hours: "", job_total: "", travel: "", stairs: "",
  amount_paid: "", paid: false, notes: "",
};

export const ManualJobs = ({ source, title, subtitle }) => {
  const [jobs, setJobs] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {..fields, id?}
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/manual-jobs`, { params: { source } });
      setJobs(data.jobs || []);
      setSummary(data.summary || {});
    } catch (e) {
      toast.error("Could not load jobs");
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const f = form;
    if (!f.customer_name.trim()) { toast.error("Customer name is required"); return; }
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      source, customer_name: f.customer_name, job_date: f.job_date, start_time: f.start_time,
      pickup: f.pickup, dropoff: f.dropoff, mover_name: f.mover_name,
      hourly_rate: num(f.hourly_rate), mover_hours: num(f.mover_hours), charged_hours: num(f.charged_hours),
      job_total: num(f.job_total), travel: num(f.travel), stairs: num(f.stairs),
      amount_paid: num(f.amount_paid), paid: !!f.paid, notes: f.notes,
    };
    try {
      if (f.id) await axios.put(`${API}/manual-jobs/${f.id}`, payload);
      else await axios.post(`${API}/manual-jobs`, payload);
      toast.success(f.id ? "Job updated" : "Job added");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      await axios.delete(`${API}/manual-jobs/${id}`);
      toast.success("Job deleted");
      setDetail(null);
      load();
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const togglePaid = async (j) => {
    try {
      await axios.put(`${API}/manual-jobs/${j.id}`, { ...j, source, paid: !j.paid });
      load();
      if (detail && detail.id === j.id) setDetail({ ...j, paid: !j.paid });
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const Field = ({ label, k, type = "text", ph = "" }) => (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
      <input
        type={type} value={form[k] ?? ""} placeholder={ph}
        onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze"
        data-testid={`manual-field-${k}`}
      />
    </label>
  );

  const cards = [
    [Inbox, "Jobs", summary.count || 0, "text-blaze"],
    [BadgeDollarSign, "Job total", aud(summary.job_total || 0, 0), "text-slate-800"],
    [CheckCircle2, "Paid to me", aud(summary.paid_total || 0, 0), "text-green-700"],
    [Clock, "Outstanding", aud(summary.outstanding || 0, 0), (summary.outstanding || 0) > 0.5 ? "text-amber-600" : "text-slate-400"],
  ];

  return (
    <div data-testid={`manual-section-${source}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
          <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
        </div>
        <button
          onClick={() => setForm({ ...EMPTY })}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-blaze px-4 py-2.5 text-sm font-bold text-white active:scale-95"
          data-testid="manual-add-btn"
        >
          <Plus className="h-4 w-4" /> Add job
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map(([Icon, label, val, cls]) => (
          <div key={label} className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
            <Icon className={`h-4 w-4 ${cls}`} />
            <p className={`mt-1.5 font-display text-xl font-bold ${cls}`}>{val}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {loading ? (
          <p className="py-8 text-center text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400" data-testid="manual-empty">No jobs yet — tap “Add job” to log one.</p>
        ) : (
          jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => setDetail(j)}
              className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition-colors hover:border-blaze/40"
              data-testid={`manual-job-${j.id}`}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-900">{j.customer_name || "—"}</p>
                <p className="text-[11px] text-slate-500">{fmtDate(j.job_date)}{j.mover_name ? ` · ${j.mover_name}` : ""}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="font-display text-sm font-bold text-slate-800">{aud(j.job_total || 0, 0)}</span>
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${j.paid ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{j.paid ? "Paid" : "Unpaid"}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Add / edit form */}
      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="manual-form-modal">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit job" : `Add ${title} job`}</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Customer name" k="customer_name" ph="John Smith" /></div>
              <Field label="Job date" k="job_date" type="date" />
              <Field label="Start time" k="start_time" ph="8:00 AM" />
              <div className="col-span-2"><Field label="Pickup" k="pickup" /></div>
              <div className="col-span-2"><Field label="Drop-off" k="dropoff" /></div>
              <Field label="Mover name" k="mover_name" />
              <Field label="Hourly rate $" k="hourly_rate" type="number" />
              <Field label="Mover hours" k="mover_hours" type="number" ph="hours logged" />
              <Field label="Charged hours" k="charged_hours" type="number" ph="platform billed" />
              <Field label="Travel $" k="travel" type="number" />
              <Field label="Stairs $" k="stairs" type="number" />
              <Field label="Job total $" k="job_total" type="number" />
              <Field label="Amount paid $" k="amount_paid" type="number" />
              <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!form.paid} onChange={(e) => setForm((s) => ({ ...s, paid: e.target.checked }))} data-testid="manual-field-paid" />
                Marked paid by platform
              </label>
              <div className="col-span-2"><Field label="Notes" k="notes" /></div>
              <div className="col-span-2 mt-1 flex gap-2">
                <button onClick={() => setForm(null)} className="flex-1 rounded-lg border border-slate-300 py-2.5 text-sm font-bold text-slate-600">Cancel</button>
                <button onClick={save} className="flex-1 rounded-lg bg-blaze py-2.5 text-sm font-bold text-white" data-testid="manual-save-btn">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto" data-testid="manual-detail-modal">
          {detail && (() => {
            const mh = detail.mover_hours, ch = detail.charged_hours;
            const diff = mh != null && ch != null ? Math.round((mh - ch) * 100) / 100 : null;
            const Row = ({ label, icon, value, cls }) => (
              <div className="flex items-center justify-between text-[12px]">
                <span className={`flex items-center gap-1 ${cls || "text-slate-500"}`}>{icon}{label}</span>
                <span className="font-semibold text-slate-800">{value}</span>
              </div>
            );
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between gap-2 pr-6">
                    <span>{detail.customer_name || "Job"}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${detail.paid ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>{detail.paid ? "Paid" : "Unpaid"}</span>
                  </DialogTitle>
                  <p className="text-left text-[11px] text-slate-400">{fmtDate(detail.job_date)}{detail.start_time ? ` · ${detail.start_time}` : ""}{detail.mover_name ? ` · ${detail.mover_name}` : ""}</p>
                </DialogHeader>
                {(detail.pickup || detail.dropoff) && (
                  <p className="text-[12px] text-slate-600">{detail.pickup} {detail.dropoff ? `→ ${detail.dropoff}` : ""}</p>
                )}

                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500"><Clock className="h-3.5 w-3.5 text-blaze" /> Hours — mover vs platform</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-blue-50 px-2 py-2"><p className="font-display text-base font-bold text-blue-700">{mh != null ? `${mh}h` : "—"}</p><p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Mover logged</p></div>
                    <div className="rounded-lg bg-slate-100 px-2 py-2"><p className="font-display text-base font-bold text-slate-700">{ch != null ? `${ch}h` : "—"}</p><p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Platform billed</p></div>
                    <div className={`rounded-lg px-2 py-2 ${diff != null && Math.abs(diff) > 0.01 ? "bg-amber-50" : "bg-slate-100"}`}><p className={`font-display text-base font-bold ${diff != null && Math.abs(diff) > 0.01 ? "text-amber-600" : "text-slate-700"}`}>{diff != null ? `${diff > 0 ? "+" : ""}${diff}h` : "—"}</p><p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Difference</p></div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Payment breakdown</p>
                  <div className="space-y-1.5">
                    {detail.hourly_rate != null && <Row label="Hourly rate" value={aud(detail.hourly_rate)} />}
                    <Row label="Travel" icon={<Navigation className="h-3 w-3 text-blaze" />} value={aud(detail.travel || 0)} />
                    <Row label="Stairs / extras" icon={<Layers className="h-3 w-3 text-orange-600" />} value={aud(detail.stairs || 0)} cls="text-orange-600" />
                    <div className="border-t border-slate-200 pt-1.5"><Row label={<b>Job total</b>} value={<b>{aud(detail.job_total || 0)}</b>} /></div>
                    <Row label="Amount paid" value={aud(detail.amount_paid || 0)} cls="text-green-700" />
                    <Row label="Outstanding" value={aud(Math.max((Number(detail.job_total) || 0) - (Number(detail.amount_paid) || 0), 0))} cls="text-amber-600" />
                  </div>
                </div>
                {detail.notes && <p className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">{detail.notes}</p>}

                <div className="flex gap-2">
                  <button onClick={() => togglePaid(detail)} className={`flex-1 rounded-lg py-2.5 text-[11px] font-bold uppercase tracking-wider ${detail.paid ? "border border-slate-300 bg-white text-slate-600" : "bg-green-600 text-white"}`} data-testid="manual-toggle-paid">{detail.paid ? "Mark unpaid" : "Mark paid"}</button>
                  <button onClick={() => { setForm({ ...detail }); setDetail(null); }} className="flex items-center justify-center gap-1 rounded-lg border border-slate-300 px-4 py-2.5 text-[11px] font-bold uppercase text-slate-700" data-testid="manual-edit-btn"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                  <button onClick={() => remove(detail.id)} className="flex items-center justify-center rounded-lg border border-red-200 px-4 py-2.5 text-red-600" data-testid="manual-delete-btn"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};
